/** external */
import _ from "lodash";
import React, { useState, memo, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { ProgressIndicator, Tag } from "@toddle-edu/ds-web";
import { Droppable, DragDropContext } from "react-beautiful-dnd";
import { connect } from "react-redux";

/** internal */
import { useI18n } from "Hooks";
import { useDebouncedCallback } from "Utils/CustomHooks";
import { TODDLE_LIBRARY } from "Constants/stringConstants";

/** relative */
import {
  WORKSHEET_MODES,
  MODE_TEACHER_VIEW,
  MODE_TEACHER_CREATE,
  MODE_TEACHER_EVALUATE,
  MODE_STUDENT_ATTEMPT,
  MODE_STUDENT_EVALUATE,
  MODE_STUDENT_VIEW,
  MODE_TEACHER_PREVIEW,
  UPDATE_DEBOUNCE_CALL_TIME,
  RESOURCE_TYPE_FOLDER,
  RESOURCE_TYPE_QUESTION,
  MODE_ASSESSMENT_VIEW,
} from "../../modules/WorksheetConstants";
import {
  getAssessmentViewQuestionsCountLabel,
  getErrorState,
  getExtractionLoaderText,
} from "../../modules/WorksheetUtils";
import {
  isWorksheetMutationsV2Enabled,
  isWorksheetQueriesV2Enabled,
} from "../../modules/WorksheetFeatureFlags";
import classes from "./SideBar.scss";
import SideBarItem from "./SideBarItem";
import AddQuestionLoader from "./AddQuestionLoader";

import ProgressBar from "./ProgressBar";
import StudentEndSidebar from "./StudentEndSidebar";
import {
  reorderOrganizationResource,
  updateSubmissionEvaluationScore,
  updateSection,
  updateActiveQuestion,
  updateVisibleQuestionAssistantIds,
  reorderQuestion,
  reorderQuestionV2,
  updateSectionV2,
} from "../../modules/WorksheetModules";
import MarksComponent from "./MarksComponent/";
import TickComponent from "./TickComponent/";

const getStudentSidebarChangeDependency = options => {
  const depsItem = [];
  _.forEach(options, option => {
    if (option?.isCompleted) depsItem.push(option?.id);
    if (!_.isEmpty(option?.childOptions)) {
      const childItems = getStudentSidebarChangeDependency(
        option?.childOptions
      );
      childItems.forEach(id => depsItem.push(id));
    }
  });
  return depsItem;
};

/**
 *
 * @param {*} params
 * @returns Worksheet Assessment Tools Button
 */

const styles = {
  progressBar: {
    width: "100%",
    backgroundColor: "var(--surface-secondary-hover)",
  },
};

const ExtractFromDocLoader = ({ t, progress, runningProgress }) => {
  const progressPercentageClasses = classNames(
    "text-body-s",
    classes.textSecondary
  );

  return (
    <div className={classes.progressContainer}>
      <div className={classes.progressTextContainer}>
        <span className="text-body-s">
          {t(getExtractionLoaderText(progress))}
        </span>
        <span
          className={progressPercentageClasses}
        >{`${runningProgress}%`}</span>
      </div>
      <ProgressIndicator
        variant="progress-bar"
        progress={runningProgress}
        status="success"
        containerStyle={styles.progressBar}
      />
    </div>
  );
};

const SideBar = memo(props => {
  const {
    options,
    questionCount,
    attemptedCount,
    evaluatedCount,
    totalMarks,
    mode,
    onAddOptionClick,
    totalScore,
    studentId,
    isAllSectionsEvaluated,
    renderEvaluation,
    isEvaluationApplicable,
    submissionStatus,
    reorderOrganizationResource,
    updateSubmissionEvaluationScore,
    updateSection,
    parentResourceId,
    toggleRef,
    errorState,
    academicTermId,
    hideHeader,
    staticOptions,
    footerComponent,
    isGeneratingAIQuestions,
    updateScoreElementRating,
    activeQuestion,
    updateActiveQuestion,
    updateVisibleQuestionAssistantIds,
    isExtracting,
    progress,
    runningProgress,
    reorderQuestion,
    reorderQuestionV2,
    parentItemId,
    updateSectionV2,
    worksheetInstructions,
    getActionsButtons,
    isFromToddleLibrary,
  } = props;

  const isNoOption = _.isEmpty(options) && _.isEmpty(staticOptions);

  const { t } = useI18n(["common", "worksheet", "classRoom"]);

  const isMutationsV2Enabled = isWorksheetMutationsV2Enabled();
  const isWorksheetQueriesV2 = isWorksheetQueriesV2Enabled(isFromToddleLibrary);

  const [isDragging, setIsDragging] = useState(false);

  const [debouncedUpdateEvaluateScoreMutation] = useDebouncedCallback(
    updateSubmissionEvaluationScore,
    UPDATE_DEBOUNCE_CALL_TIME
  );

  const [debouncedUpdateSectionMutation] = useDebouncedCallback(
    isMutationsV2Enabled ? updateSectionV2 : updateSection,
    UPDATE_DEBOUNCE_CALL_TIME
  );

  const onUpdateActiveQuestion = useCallback(
    (...args) => {
      updateVisibleQuestionAssistantIds([]);
      updateActiveQuestion(...args);
    },
    [updateActiveQuestion, updateVisibleQuestionAssistantIds]
  );

  const isTeacherCreateMode = mode === MODE_TEACHER_CREATE;

  const shouldHaveFixedWidth = !_.includes(
    [MODE_TEACHER_EVALUATE, MODE_TEACHER_VIEW],
    mode
  );

  const headerTitleClasses = classNames(
    classes.sideBarHeaderTitle,
    classes.sideBarItem,
    {
      [classes.sideBarHeaderTitleEvalMode]: _.includes(
        [MODE_TEACHER_EVALUATE, MODE_TEACHER_VIEW],
        mode
      ),
    }
  );

  const headerContentClasses = classNames(
    classes.sideBarHeaderContent,
    classes.sideBarItem
  );

  const questionCountLabelClasses = classNames(
    classes.questionCountLabel,
    "text-label-3"
  );

  const totalMarksLabelClasses = classNames(
    classes.totalMarksLabel,
    "text-body-2"
  );

  const sideBarClasses = classNames({
    [classes.sideBar]: true,
    [classes.createModeSidebar]: isTeacherCreateMode,
    [classes.assessmentViewSidebar]: mode == MODE_ASSESSMENT_VIEW,
    [classes.sideBarFixedWidth]: shouldHaveFixedWidth,
    [classes.cursor]: isDragging && isTeacherCreateMode,
    [classes.createModeRevampV2]: isTeacherCreateMode,
  });

  const sideBarEvalViewHeaderClasses = classNames(
    classes.sideBarHeader,
    classes.evaluationViewHeader
  );

  const getHeaderContent = () => {
    switch (mode) {
      case MODE_TEACHER_CREATE:
        return (
          <div className={classes.sideBarHeader}>
            <div className={headerTitleClasses}>
              <span className="text-pre-title-1">{t("common:outline")}</span>
            </div>
            <div className={headerContentClasses}>
              <span className={questionCountLabelClasses}>
                {t("common:question_number", { count: questionCount })}
              </span>
              <span className={totalMarksLabelClasses}>
                {t("worksheet:total_marks_with_count", {
                  count: totalMarks,
                })}
              </span>
            </div>
          </div>
        );
      case MODE_TEACHER_VIEW:
      case MODE_TEACHER_EVALUATE:
        return (
          <div className={sideBarEvalViewHeaderClasses}>
            <div className={classes.container}>
              <div className={headerTitleClasses}>
                <div>
                  <span className="text-label-2">
                    {t("worksheet:questions")}
                  </span>
                </div>
                <div className={classes.questionSubtextContainer}>
                  <div className="text-body-3">
                    {t("worksheet:count_and_totalCount_with_label", {
                      count: evaluatedCount,
                      totalCount: questionCount,
                      label: t("classRoom:evaluated"),
                    })}
                  </div>
                  <div className={classes.dotContainer}></div>
                  <div className="text-body-3">
                    {t("worksheet:count_and_totalCount_with_label", {
                      count: attemptedCount,
                      totalCount: questionCount,
                      label: t("worksheet:attempted_by_student"),
                    })}
                  </div>
                </div>
              </div>
              <div className={classes.actionButtonsContainer}>
                {getActionsButtons()}
              </div>
            </div>
            <div className={classes.marksView}>
              <MarksComponent
                maxMarks={totalMarks}
                score={totalScore}
                isQuestion={false}
                mode={mode}
                isTeacherEvaluationHeader={true}
              />
              <TickComponent isCompleted={isAllSectionsEvaluated} />
            </div>
          </div>
        );
      case MODE_STUDENT_ATTEMPT:
      case MODE_STUDENT_VIEW:
      case MODE_STUDENT_EVALUATE:
        return (
          <ProgressBar
            t={t}
            questionCount={questionCount}
            attemptedCount={attemptedCount}
          />
        );
      case MODE_TEACHER_PREVIEW:
        return (
          <div className={classes.teacherPreviewSideBarHeader}>
            <div className={headerContentClasses}>
              <span className={questionCountLabelClasses}>
                {t("common:question_number", { count: questionCount })}
              </span>
              <span className={totalMarksLabelClasses}>
                {t("worksheet:total_marks_with_count", {
                  count: totalMarks,
                })}
              </span>
            </div>
          </div>
        );
      case MODE_ASSESSMENT_VIEW: {
        const questionsCountLabels = getAssessmentViewQuestionsCountLabel({
          options,
          t,
        });
        return (
          <div className={classes.assessmentViewSidebarHeaderContainer}>
            <div className={classes.assessmentViewSidebarHeader}>
              <div className="text-label-2">
                {t("common:question_number", { count: questionCount })}
              </div>
              <Tag
                _legacy_size="x-small"
                size="small"
                color="yellow"
                dsVersion="2.0"
              >
                {t("common:mark_with_number", { count: totalMarks })}
              </Tag>
            </div>
            {!_.isEmpty(questionsCountLabels) && (
              <div
                className={classNames(
                  "text-body-2",
                  classes.assessmentViewSidebarSubHeader
                )}
              >
                {_.map(questionsCountLabels, option => (
                  <div>{option}</div>
                ))}
              </div>
            )}
          </div>
        );
      }
      default:
        return null;
    }
  };

  const onDragStart = () => {
    setIsDragging(true);
  };

  const onDragEnd = async params => {
    setIsDragging(false);
    const resourceType = _.get(params, "type", "");

    const fromIndex = _.get(params, "source.index", 0);
    const toIndex = _.get(params, "destination.index", 0);
    /** if mutationV2 is not enabled then this is draggableResourceId */
    const draggableId = _.get(params, "draggableId");

    const oldParentId = _.get(params, "source.droppableId", null);
    const newParentId = _.get(params, "destination.droppableId", null);

    // For cross-section moves, we need different logic for putAfterItemIndex
    const isCrossSectionMove = oldParentId !== newParentId;
    const putAfterItemIndex =
      isCrossSectionMove && isWorksheetQueriesV2
        ? toIndex > 0
          ? toIndex - 1
          : -1 // For cross-section: use toIndex - 1, or -1 for position 0
        : toIndex <= fromIndex
        ? toIndex - 1
        : toIndex; // For same-section: use existing logic

    if (_.isNil(_.get(params, "destination")) || _.isNil(draggableId)) return;

    if (resourceType == RESOURCE_TYPE_FOLDER) {
      /** Section reordering */

      if (toIndex == fromIndex) {
        return;
      }

      if (isMutationsV2Enabled) {
        // Use the updated mutations v2 API for section reordering
        // This handles the reordering by using section item IDs instead of resource IDs
        const putAfterQuestionId = _.get(
          options,
          [putAfterItemIndex, "parentItemId"],
          null
        );
        const reorderQuestionFn = isWorksheetQueriesV2
          ? reorderQuestionV2
          : reorderQuestion;
        await reorderQuestionFn({
          questionId: draggableId,
          putAfterQuestionId,
          fromIndex,
          toIndex,
          oldParentId: parentItemId,
          newParentId: parentItemId,
          resourceType,
        });
      } else {
        const putAfterItemId = _.get(
          options,
          [putAfterItemIndex, "resourceId"],
          null
        );
        await reorderOrganizationResource({
          resourceType,
          id: draggableId, //this is dragabbleResourceId
          putAfterItemId,
          oldParentResourceId: parentResourceId,
          newParentResourceId: parentResourceId,
          fromIndex,
          toIndex,
        });
      }
    } else if (resourceType == RESOURCE_TYPE_QUESTION) {
      /** Question reordering */

      if (toIndex == fromIndex && oldParentId == newParentId) {
        return;
      }

      if (isMutationsV2Enabled) {
        // Get the ID of the question after which the dragged question will be placed
        const putAfterQuestionId = _.get(
          _.find(options, { parentItemId: newParentId }),
          ["childOptions", putAfterItemIndex, "id"],
          null
        );
        const reorderQuestionFn = isWorksheetQueriesV2
          ? reorderQuestionV2
          : reorderQuestion;

        // Call reorderQuestion with all necessary parameters for the mutation
        await reorderQuestionFn({
          questionId: draggableId,
          putAfterQuestionId,
          fromIndex,
          toIndex,
          oldParentId,
          newParentId,
          resourceType,
        });
      } else {
        const putAfterItemId = _.get(
          _.find(options, { resourceId: newParentId }),
          ["childOptions", putAfterItemIndex, "resourceId"],
          null
        );

        await reorderOrganizationResource({
          resourceType,
          id: draggableId,
          putAfterItemId,
          oldParentResourceId: oldParentId,
          newParentResourceId: newParentId,
          fromIndex,
          toIndex,
        });
      }
    }
  };

  const getSidebarItems = (option, index) => {
    const questionId = _.get(option, "id");

    const showWarningIcon = isTeacherCreateMode
      ? _.get(errorState, questionId, false)
      : false;

    const showLoader =
      isGeneratingAIQuestions &&
      _.isEqual(_.get(_.last(options), "id", ""), questionId);

    return (
      <>
        <SideBarItem
          toggleRef={toggleRef}
          key={questionId}
          mode={mode}
          studentId={studentId}
          renderEvaluation={renderEvaluation}
          isEvaluationApplicable={isEvaluationApplicable}
          submissionStatus={submissionStatus}
          index={index}
          debouncedUpdateEvaluateScoreMutation={
            debouncedUpdateEvaluateScoreMutation
          }
          debouncedUpdateSectionMutation={debouncedUpdateSectionMutation}
          errorState={errorState}
          showWarningIcon={showWarningIcon}
          academicTermId={academicTermId}
          updateScoreElementRating={updateScoreElementRating}
          totalMarks={totalMarks}
          isFromToddleLibrary={isFromToddleLibrary}
          {...option}
        />
        {showLoader && <AddQuestionLoader />}
      </>
    );
  };

  const contentClassName = classNames(classes.content, {
    [classes.questionContainer]: mode === MODE_TEACHER_CREATE,
    [classes.noQuestionsAddedContainer]:
      _.isEmpty(options) && mode === MODE_ASSESSMENT_VIEW,
  });

  const noQuestionsAddedClasses = classNames(
    classes.noQuestionsAdded,
    "text-body"
  );

  const renderNewSidebar = _.includes(
    [MODE_STUDENT_ATTEMPT, MODE_TEACHER_PREVIEW],
    mode
  );

  const optionsDeps = useMemo(() => {
    return renderNewSidebar
      ? getStudentSidebarChangeDependency(options).join()
      : "";
  }, [renderNewSidebar, options]);
  const studentOptions = useMemo(() => options, [optionsDeps]);

  return renderNewSidebar ? (
    <StudentEndSidebar
      staticOptions={staticOptions}
      options={studentOptions}
      progress={progress}
      runningProgress={runningProgress}
      questionCount={questionCount}
      totalMarks={totalMarks}
      attemptedCount={attemptedCount}
      updateActiveQuestion={onUpdateActiveQuestion}
      activeQuestion={activeQuestion}
      worksheetInstructions={worksheetInstructions}
      onInstructionsClick={_.get(props, "onInstructionsClick")}
      mode={mode}
    />
  ) : (
    <div className={sideBarClasses}>
      {/* SIDEBAR HEADER */}
      {hideHeader ? null : getHeaderContent()}

      {/* EXTRACT FROM DOCS/FORMS LOADER */}
      {isExtracting ? (
        <ExtractFromDocLoader
          t={t}
          progress={progress}
          runningProgress={runningProgress}
        />
      ) : null}

      {/* QUESTION AND SECTION CONTAINER */}
      <div className={contentClassName}>
        {!isNoOption ? (
          <DragDropContext onDragEnd={onDragEnd} onDragStart={onDragStart}>
            <Droppable
              droppableId={"WORKSHEET_SECTIONS"}
              type={RESOURCE_TYPE_FOLDER}
            >
              {provided => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  aria-label="worksheet-sections"
                  role="group"
                >
                  {_.map(options, getSidebarItems)}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        ) : null}
        {_.isEqual(mode, MODE_ASSESSMENT_VIEW) && isNoOption && (
          <span className={noQuestionsAddedClasses}>
            {t("worksheet:no_questions_added")}
          </span>
        )}
        {React.isValidElement(footerComponent) ? footerComponent : null}
      </div>
    </div>
  );
});

SideBar.propTypes = {
  options: PropTypes.arrayOf(PropTypes.object),
  totalCount: PropTypes.number,
  questionCount: PropTypes.number,
  attemptedCount: PropTypes.number,
  evaluatedCount: PropTypes.number,
  totalMarks: PropTypes.number,
  mode: PropTypes.oneOf(WORKSHEET_MODES),
  onAddOptionClick: PropTypes.func,
  isAllSectionsEvaluated: PropTypes.bool,
  renderEvaluation: PropTypes.func,
  isEvaluationApplicable: PropTypes.bool,
  submissionStatus: PropTypes.string,
  hideHeader: PropTypes.bool,
  staticOptions: PropTypes.arrayOf(PropTypes.object),
  footerComponent: PropTypes.element,
  updateScoreElementRating: PropTypes.func,
  isExtracting: PropTypes.bool,
  runningProgress: PropTypes.number,
  isToddleLibraryPortal: PropTypes.bool,
};

SideBar.defaultProps = {
  options: [],
  questionCount: 0,
  attemptedCount: 0,
  evaluatedCount: 0,
  isAllSectionsEvaluated: false,
  totalMarks: 0,
  onAddOptionClick: _.noop,
  mode: MODE_TEACHER_VIEW,
  hideHeader: false,
  staticOptions: [],
  footerComponent: null,
  isExtracting: false,
  runningProgress: 0,
  isToddleLibraryPortal: false,
};

const mapStateToProps = (state, ownProps) => {
  const { mode } = ownProps;

  // * Showing error only when clicking assign
  const canShowErrors = _.get(state, "worksheet.sidebar.showErrors", false);
  const errorState = canShowErrors
    ? getErrorState({
        mode,
        errorStateData: _.get(state, ["worksheet", "errorState"], {}),
      })
    : {};

  const isGeneratingAIQuestions = _.get(
    state,
    "worksheet.generatingAIQuestions",
    false
  );

  const visibleQuestionAssistantIds = _.get(
    state,
    "worksheet.visibleQuestionAssistantIds",
    []
  );

  const activeQuestion = _.get(state, "worksheet.activeQuestion");
  const isWorksheetAssistantOpen = _.includes(
    visibleQuestionAssistantIds,
    activeQuestion
  );

  const isToddleLibraryPortal =
    _.get(state, "login.activeTab", "") === TODDLE_LIBRARY;

  return {
    errorState,
    isGeneratingAIQuestions,
    activeQuestion,
    isWorksheetAssistantOpen,
    isToddleLibraryPortal,
  };
};

const mapActionCreators = {
  reorderOrganizationResource,
  updateSubmissionEvaluationScore,
  updateSection,
  updateActiveQuestion,
  updateVisibleQuestionAssistantIds,
  reorderQuestion,
  reorderQuestionV2,
  updateSectionV2,
};

SideBar.displayName = "SideBar";

export default connect(mapStateToProps, mapActionCreators)(SideBar);
